import uid from "common/utils/uid";
import { DIST_EPSILON } from "geom/core";
import type { DocMigration } from "../docMigration";

const LEGACY_DORMER_CORNER_BOARD_WIDTH = 2;

const defaultEmptyViewNote = {
  enable: false,
  tagOnlyOne: false,
  text: "",
};

const defaultCornerBoardNote = {
  enable: true,
  text: "CORNERBOARD",
  tagOnlyOne: true,
};

const defaultAllExteriorTrimViewNotes = {
  ...{
    ["OUTSIDE_CORNER"]: defaultCornerBoardNote,
    ["INSIDE_CORNER"]: defaultEmptyViewNote,
    ["DORMER_CORNER"]: defaultEmptyViewNote,
    ["WALL_ROOF_BASEBOARD"]: defaultEmptyViewNote,
    ["PORCH_HEADER_TRIM"]: defaultEmptyViewNote,
  },
};

const defaultProfileSettings = {
  type: "PROFILE_CATALOG_SETTINGS",
  profileTable: {},
};

const defaultExteriorTrimSettings = {
  type: "EXTERIOR_TRIM_SETTINGS",
  enableOutsideCornerMoulding: true,
  enableInsideCornerMoulding: false,
  wallRoofBaseBoard: "NONE",
  wallRoofBaseBoardFilter: "ALL_EDGES",
  viewNotes: defaultAllExteriorTrimViewNotes,
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_139: DocMigration = {
  maxSchemaVersion: "0.0.139",
  description: "Custom profiles for corner boards",
  apply(json: any) {
    const { settings: allSettings, dormers } = json;

    // look for custom corner board widths in the exterior trim settings
    const cornerBoardWidthToProfileId: { [key: string]: string } = {};
    const customCornerBoardProfiles: { [key: string]: any } = {};

    const addCustomProfile = (width: number): string | undefined => {
      let profileId: string | undefined = undefined;

      if (Math.abs(width - LEGACY_DORMER_CORNER_BOARD_WIDTH) > DIST_EPSILON) {
        profileId = cornerBoardWidthToProfileId[width];
        if (!profileId) {
          profileId = uid();
          customCornerBoardProfiles[profileId] = {
            name: `Corner Board 1 x ${width}`,
            entityType: "PROFILE_EXTRUSION",
            profileType: "BOARD",
            elevationNoteEnabled: false,
            width: 1,
            height: width,
            nominalWidth: 1,
            nominalHeight: 4,
            elevationNote: `${width}"`,
          };
          cornerBoardWidthToProfileId[width] = profileId;
        }
      }

      return profileId;
    };

    const addCustomProfileFromSettings = (roofSettings: any): string | undefined => {
      const { dormerCornerBoardSize } = roofSettings;

      if (dormerCornerBoardSize === undefined) {
        return undefined;
      }

      const profileId = addCustomProfile(dormerCornerBoardSize);

      delete roofSettings.dormerCornerBoardSize;

      return profileId;
    };

    const addCustomProfileFromDormer = (dormer: any): string | undefined => {
      const { cornerBoardSize } = dormer;

      if (cornerBoardSize === undefined) {
        return undefined;
      }

      const profileId = addCustomProfile(cornerBoardSize);

      delete dormer.cornerBoardSize;

      return profileId;
    };

    for (const dormerId in dormers) {
      const dormer = dormers[dormerId];

      const profileId = addCustomProfileFromDormer(dormer);

      if (!profileId) {
        continue;
      }

      dormer.cornerBoardProfile = profileId;
    }

    for (const settingsId in allSettings) {
      const settings = allSettings[settingsId];
      if (settings.type !== "ROOF_SETTINGS") {
        continue;
      }

      const profileId = addCustomProfileFromSettings(settings);

      if (!profileId) {
        continue;
      }

      // look for the parent settings set
      const settingsSetId = settings.parentId;

      // look for exterior trim settings on this setting set
      let exteriorTrimSettingsId: string | undefined = undefined;
      for (const settingsId in allSettings) {
        const settings = allSettings[settingsId];
        if (settings.parentId === settingsSetId && settings.type === "EXTERIOR_TRIM_SETTINGS") {
          exteriorTrimSettingsId = settingsId;
        }
      }

      // if not found, create a new one
      if (!exteriorTrimSettingsId) {
        exteriorTrimSettingsId = uid();
        allSettings[exteriorTrimSettingsId] = {
          ...defaultExteriorTrimSettings,
          entityType: "SETTINGS",
          id: exteriorTrimSettingsId,
          parentId: settingsSetId,
          sortIndex: 0,
        };
      }

      // set the profile
      allSettings[exteriorTrimSettingsId].dormerCornerBoardProfile = profileId;
    }

    if (Object.keys(customCornerBoardProfiles).length > 0) {
      // create a first settings set if it doesn't exist. Reference first settings set id.
      let firstSettingsSetId: string | undefined = undefined;
      for (const settingsSetId in json.settingsSets) {
        const settingsSet = json.settingsSets[settingsSetId];
        if (settingsSet.sortIndex !== 0) {
          continue;
        }
        firstSettingsSetId = settingsSet.id;
      }

      if (firstSettingsSetId === undefined) {
        const baseSettingsSetId = uid();
        if (!json.settingsSets) {
          (json.settingsSets as any) = {};
        }

        (json.settingsSets[baseSettingsSetId] as any) = {
          entityType: "SETTINGS_SET",
          name: "Base Settings Set",
          id: baseSettingsSetId,
          sortIndex: 0,
        };
        firstSettingsSetId = baseSettingsSetId;
      }

      // add this profiles to an existing profile calatog or create a new one
      let firstSettingsSetProfileSettingsId: string | undefined = undefined;

      for (const settingsId in allSettings) {
        const settings = allSettings[settingsId] as any;
        if ((settings.type as any) === "PROFILE_CATALOG_SETTINGS") {
          if (firstSettingsSetId !== undefined && settings.parentId !== undefined) {
            if (settings.parentId === firstSettingsSetId) {
              firstSettingsSetProfileSettingsId = settingsId;
              (allSettings[settingsId] as any) = {
                ...settings,
                profileTable: { ...settings.profileTable, ...customCornerBoardProfiles },
              };
            }
          }
        }
      }

      if (!firstSettingsSetProfileSettingsId) {
        const newSettingsId = uid();
        (allSettings[newSettingsId] as any) = {
          ...{ ...defaultProfileSettings, profileTable: customCornerBoardProfiles },
          entityType: "SETTINGS",
          id: newSettingsId,
          parentId: firstSettingsSetId,
          sortIndex: 0,
        };
      }
    }

    return json;
  },
};

export default migration_0_0_139;
